import { Link } from "react-router-dom";
import {
  CoachOverviewClient,
  IssueFlag,
  BillingNotice,
  useAcknowledgeFlag,
  useDecidePlanRequest,
  useMarkBillingNoticeSeen,
} from "../lib/queries";
import { money } from "./PricingSettings";
import { describeAreas } from "../lib/checkin";
import { ErrorState } from "./Loading";

/**
 * Reports of something wrong, at the top of the coach's home.
 *
 * The failure this closes is the sharpest one in the review: an athlete ticked "flag an issue", wrote
 * that her knee hurt on the landings, and stopped the session -- which is exactly what a family should
 * do -- and the coach saw nothing at all, because the flag was an attribute of a *completed* session
 * (UX review F72). Even when a session was finished, the flag was only findable by opening that client
 * and then that session.
 *
 * So it sits above the roster rather than inside it. Safeguarding information that has to be gone
 * looking for is not information the coach has.
 */
export function OpenFlags({
  clients,
  single = false,
}: {
  clients: CoachOverviewClient[];
  /** On one athlete's own page, where naming them on every row would only repeat the page's title. */
  single?: boolean;
}) {
  const acknowledge = useAcknowledgeFlag();
  const decide = useDecidePlanRequest();
  const markSeen = useMarkBillingNoticeSeen();

  const flags = clients
    .flatMap((client) => (client.open_flags ?? []).map((flag) => ({ flag, client })))
    // Newest first: the thing reported an hour ago matters more than the thing reported on Tuesday.
    .sort((a, b) => (a.flag.created_at < b.flag.created_at ? 1 : -1));

  /**
   * Athletes who have gone quiet.
   *
   * The other half of "who needs me" (UX review F38). A coach signing in landed on a client list --
   * a search box, two filters and eighteen rows all looking equally fine -- so an athlete who simply
   * stopped training was indistinguishable from one who trained yesterday until he opened them one by
   * one. Silence is the most common thing that needs a coach's attention and the hardest to see.
   *
   * Only athletes with a published plan: somebody who has not been given anything to do has not gone
   * quiet, and listing them would be blaming them for the coach's own backlog. Never-trained is called
   * out separately from lapsed, because they need different conversations.
   */
  const quiet = clients
    .filter((client) => client.has_published_plan && client.status === "active")
    .map((client) => ({ client, lastAt: latestSessionAt(client) }))
    .filter(({ client, lastAt }) =>
      lastAt === null ? publishedLongEnoughToExpectASession(client) : daysSince(lastAt) >= QUIET_DAYS
    )
    .sort((a, b) => (a.lastAt ?? "").localeCompare(b.lastAt ?? ""));

  /**
   * A family asking to switch, and families who switched or cancelled on their own (27 Sep debug notes
   * item 12). Requests first: they are the only ones waiting on the coach to do something.
   */
  const requests = clients.filter((client) => client.plan_request?.status === "pending");
  const notices = clients
    .flatMap((client) => (client.billing_notices ?? []).map((notice) => ({ notice, client })))
    .sort((a, b) => (a.notice.created_at < b.notice.created_at ? 1 : -1));

  // Nothing to say when nothing is wrong. A permanent empty "no issues" panel would train the coach to
  // ignore the space this occupies, which is the one thing it must never become.
  if (flags.length === 0 && quiet.length === 0 && requests.length === 0 && notices.length === 0) return null;

  return (
    <section className="open-flags" aria-labelledby="open-flags-heading">
      <h2 id="open-flags-heading">Needs your attention</h2>
      {acknowledge.isError && <ErrorState error={acknowledge.error} />}
      {decide.isError && <ErrorState error={decide.error} />}
      {markSeen.isError && <ErrorState error={markSeen.error} />}

      {requests.length > 0 && (
        <ul className="open-flags-list">
          {requests.map((client) => (
            <li key={`request-${client.id}`} className="open-flag">
              <div className="open-flag-body">
                <p className="open-flag-who">
                  {!single && <Link to={`/coach/clients/${client.id}`}>{client.name}</Link>}
                  <span className="open-flag-when">{describeWhen(client.plan_request!.requested_at)}</span>
                </p>
                <p className="open-flag-what">
                  Their family asked to switch to {client.plan_request!.plan_name ?? "another plan"}
                  {client.plan_request!.plan_price_cents !== null
                    ? ` (${money(client.plan_request!.plan_price_cents)}/mo)`
                    : ""}
                  .
                </p>
              </div>
              <div className="open-flag-actions">
                <button
                  type="button"
                  className="chip active"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ clientId: client.id, outcome: "approve" })}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="chip"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ clientId: client.id, outcome: "decline" })}
                >
                  Decline
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <ul className="open-flags-list">
        {flags.map(({ flag, client }) => (
          <li key={flag.id} className={flag.session_ended ? "open-flag stopped" : "open-flag"}>
            <div className="open-flag-body">
              <p className="open-flag-who">
                {!single && <Link to={`/coach/clients/${client.id}`}>{client.name}</Link>}
                <span className="open-flag-when">{describeWhen(flag.created_at)}</span>
              </p>
              <p className="open-flag-what">{describeFlag(flag)}</p>
              {flag.note && <p className="open-flag-note">&ldquo;{flag.note}&rdquo;</p>}
            </div>
            <button
              type="button"
              className="chip"
              disabled={acknowledge.isPending}
              onClick={() => acknowledge.mutate({ clientId: client.id, flagId: flag.id })}
            >
              Mark as seen
            </button>
          </li>
        ))}
      </ul>

      {notices.length > 0 && (
        <ul className="open-flags-list">
          {notices.map(({ notice, client }) => (
            <li key={notice.id} className="open-flag">
              <div className="open-flag-body">
                <p className="open-flag-who">
                  {!single && <Link to={`/coach/clients/${client.id}`}>{client.name}</Link>}
                  <span className="open-flag-when">{describeWhen(notice.created_at)}</span>
                </p>
                <p className="open-flag-what">{describeNotice(notice)}</p>
              </div>
              <button
                type="button"
                className="chip"
                disabled={markSeen.isPending}
                onClick={() => markSeen.mutate({ clientId: client.id, noticeId: notice.id })}
              >
                Mark as seen
              </button>
            </li>
          ))}
        </ul>
      )}

      {quiet.length > 0 && (
        <ul className="open-flags-list quiet-list">
          {quiet.map(({ client, lastAt }) => (
            <li key={client.id} className="open-flag">
              <div className="open-flag-body">
                <p className="open-flag-who">
                  {!single && <Link to={`/coach/clients/${client.id}`}>{client.name}</Link>}
                </p>
                <p className="open-flag-what">
                  {lastAt === null
                    ? "Has a plan but has never logged a session."
                    : `No session logged for ${daysSince(lastAt)} days.`}
                </p>
              </div>
              <Link className="chip" to={`/coach/clients/${client.id}/messages`}>
                Message
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** How long counts as gone quiet. A week covers a missed session; two makes it a pattern. */
const QUIET_DAYS = 10;

/**
 * How long a plan has to have been live before never having trained against it is worth reporting.
 *
 * Without this the coach publishes a plan and the athlete appears on the attention list in the same
 * minute, for the crime of not having trained yet — which turns the panel into a list of the coach's
 * own recent work and trains them to ignore it. A week is long enough that a family who intended to
 * start has, and short enough that a family who quietly did not is still worth a message.
 */
const GRACE_DAYS_AFTER_PUBLISH = 7;

/**
 * Treats an unknown publish date as "long enough".
 *
 * `published_at` is null on plans published before it was recorded, and absent entirely on an older
 * overview response. Staying silent in that case would hide genuinely quiet athletes forever, which is
 * the failure this panel exists to prevent; reporting them costs the coach one glance.
 */
function publishedLongEnoughToExpectASession(client: CoachOverviewClient, now = Date.now()): boolean {
  const publishedAt = client.current_plan?.published_at;
  if (!publishedAt) return true;
  return daysSince(publishedAt, now) > GRACE_DAYS_AFTER_PUBLISH;
}

function latestSessionAt(client: CoachOverviewClient): string | null {
  const dates = (client.sessions ?? []).map((session) => session.completed_at).filter(Boolean);
  return dates.length > 0 ? dates.reduce((latest, at) => (at > latest ? at : latest)) : null;
}

function daysSince(iso: string, now = Date.now()): number {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return 0;
  return Math.floor((now - at) / (24 * 60 * 60 * 1000));
}

/**
 * What happened, in a line. "They stopped" leads, because it is the difference between something to
 * read later and something to ring a parent about.
 */
function describeFlag(flag: IssueFlag): string {
  const who = flag.reported_by_role === "guardian" ? "Their guardian" : "They";
  // From "Is anything hurting?" before a workout. Whether they then trained is the thing to know.
  if (flag.source === "checkin") {
    const areas = describeAreas(flag.pain_areas ?? []);
    const what = areas ? `their ${areas} hurts` : "something hurts";
    return flag.session_ended
      ? `${who} said before the workout that ${what}, and didn't train today.`
      : `${who} said before the workout that ${what}, and chose to train, going easy.`;
  }
  const where = flag.exercise_name
    ? `on ${flag.exercise_name}${flag.set_index !== null ? `, set ${flag.set_index + 1}` : ""}`
    : "during a session";
  return flag.session_ended
    ? `${who} reported a problem ${where} and stopped the session there.`
    : `${who} reported a problem ${where}.`;
}

/** A family's own billing change, in a line. When it lands is said, because that is when to talk. */
function describeNotice(notice: BillingNotice): string {
  const when = notice.effective_at
    ? ` on ${new Date(notice.effective_at).toLocaleDateString(undefined, { month: "long", day: "numeric" })}`
    : "";
  return notice.kind === "cancelled"
    ? `Their family cancelled; coaching ends${when || " at the end of the paid month"}.`
    : `Their family switched to ${notice.plan_name ?? "another plan"}${when ? `, starting${when}` : ""}.`;
}

/** Relative, because "2 hours ago" is what decides whether this is dealt with now or after lunch. */
function describeWhen(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}
